<script setup lang="ts">
import CloseIcon from '~/assets/icons/digital/close.svg'

const props = defineProps<{
  context: {
    value?: string | string[]
    node: { input: (v?: string | string[] | null) => void }
    where?: Record<string, any>
    disabled?: boolean
    selectionDisabled?: boolean
    hint?: string
    multiple?: boolean
    hideAddress?: boolean
    disablePersonCreation?: boolean
    translationKey?: string
  }
}>()

const selectedPersonId = computed(() => {
  if (typeof props.context.value === 'string' && props.context.value) {
    return props.context.value
  }
  return undefined
})

const { personQuery } = useQuery()
const { data: personData, isLoading } = personQuery.findOne(computed(() => ({ id: selectedPersonId.value })))

// Reset Input value if the person cannot be found
watch(personData, (value) => {
  if (value === null) {
    handleSelect()
  }
})

const isPersonSelectorOpen = ref(false)
const { openAccountPopup: { data: accountPopupData, ...openAccountPopup } } = useOpeners()

const handleSelect = (input?: string | string[]) => {
  if (!input) {
    return props.context.node.input(null)
  }
  if (!props.context.multiple) {
    props.context.node.input(input)
    return isPersonSelectorOpen.value = false
  }

  if (Array.isArray(input)) {
    return props.context.node.input(input)
  }
  if (Array.isArray(props.context.value)) {
    return props.context.node.input([...props.context.value, input])
  }
  return props.context.node.input([input])
}
</script>

<template>
  <div>
    <div v-if="!isLoading" class="py-3 px-4 bg-primary-10 rounded">
      <n-collapse :trigger-areas="['main', 'arrow']" arrow-placement="right">
        <n-collapse-item name="personInfos" :disabled="!personData || context.disabled">
          <template #header>
            <div class="flex flex-wrap items-start gap-1">
              <div class="flex flex-1 items-center gap-1">
                <p class="text-base flex-grow">
                  <template v-if="personData">
                    {{ personData.firstName }} {{ personData.lastName }}
                    <span v-if="personData.birthDate">
                      ({{ $d(personData.birthDate) }})
                    </span>
                  </template>
                  <template v-else-if="context.multiple">
                    {{ $t(`person.selector.${context.translationKey ?? 'default'}.length`, context.value?.length || 0) }}
                  </template>
                  <template v-else>
                    {{ $t(`person.selector.${context.translationKey ?? 'default'}.prompt`) }}
                  </template>
                </p>
              </div>

              <div v-if="!context.selectionDisabled" class="w-full sm:hidden">
                <n-button
                  class="whitespace-normal break-words w-full mt-2"
                  type="primary"
                  :disabled="context.disabled"
                  @click="isPersonSelectorOpen = true"
                >
                  {{ $t(`person.selector.${context.translationKey ?? 'default'}.select`) }}
                </n-button>
              </div>
            </div>
          </template>
          <template v-if="!context.selectionDisabled" #header-extra>
            <div class="flex items-center gap-1">
              <n-button
                v-if="context.value && context.value.length >= 1"
                circle
                quaternary
                class="mt-0.5"
                :disabled="context.disabled"
                @click="handleSelect()"
              >
                <div class="h-6 w-6">
                  <CloseIcon class="h-5 w-5" />
                </div>
              </n-button>
              <div class="hidden w-full sm:flex">
                <n-button
                  class="whitespace-normal break-words w-full sm:w-auto mt-2 sm:mt-0"
                  type="primary"
                  :disabled="context.disabled"
                  @click="isPersonSelectorOpen = true"
                >
                  {{ $t(`person.selector.${context.translationKey ?? 'default'}.select`) }}
                </n-button>
              </div>
            </div>
          </template>
          <template v-if="!personData || context.disabled" #arrow>
            <span />
          </template>

          <div class="border-t-2 pt-3 border-primary-50/50">
            <PersonDetailsList
              v-if="personData"
              :person="personData"
              :styling="{ layout: 'grid', size: 'sm', wrapperClass: 'xl:grid-cols-5' }"
              :hidden-keys="['address', 'livingSituation']"
            />
          </div>
        </n-collapse-item>
      </n-collapse>
      <LazyThePopup
        v-if="isPersonSelectorOpen && !context.selectionDisabled"
        :show="isPersonSelectorOpen"
        :title="$t(`person.selector.${context.translationKey ?? 'default'}.select`)"
        width="1200px"
        class=" max-w-full"
        @close="isPersonSelectorOpen = false"
      >
        <div v-if="!context.disablePersonCreation" class="flex items-center gap-2 justify-end mb-4">
          <n-button type="primary" @click="openAccountPopup.open({ mode: 'create' })">
            <template #icon>
              <AddIcon />
            </template>
            {{ $t('account.citizenPage.create') }}
          </n-button>
        </div>
        <div class="overflow-x-auto">
          <PersonOverviewTable
            :where="context.where"
            :checked-row-keys="Array.isArray(context.value) ? context.value : undefined"
            :is-selectable="context.multiple"
            :actions="context.multiple ? 'false' : ['select']"
            :hide-address="context.hideAddress"
            @select="row => handleSelect(row.id)"
            @update:checked-row-keys="keys => handleSelect(keys as string[])"
          />
        </div>
        <div class="flex items-center gap-2 pt-3 pb-3">
          <n-button v-if="context.multiple" type="primary" size="medium" @click="isPersonSelectorOpen = false">
            {{ $t('button.selection.confirm') }}
          </n-button>
          <n-button size="medium" @click="isPersonSelectorOpen = false">
            {{ $t('button.close') }}
          </n-button>
        </div>
      </LazyThePopup>
      <LazyAccountCreateOrUpdatePopup
        v-if="accountPopupData"
        :opener="accountPopupData"
        :options="{ showPersonDetails: true }"
        :creation-roles="['person']"
        @submit="account => handleSelect(account?.personId ?? undefined)"
        @close="openAccountPopup.close()"
      />
    </div>
    <NSkeleton v-else size="medium" :sharp="false" height="48px" />
  </div>
  <p v-if="context.hint" class="text-xs text-gray-400 pt-1">
    {{ context.hint }}
  </p>
</template>
